import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import FileOperationModal from "../../components/FileOperationModal";
import PrintRequestModal from "../../components/PrintRequestModal";
import TemporaryLinkModal from "../../components/TemporaryLinkModal";
import ShareLinkModal from "../../components/ShareLinkModal";
import { FaShareAlt } from "react-icons/fa";
import FileListNameCell from "../../components/FileListNameCell";
import FileUploadMetaFields from "../../components/FileUploadMetaFields";
import { PublishToTradeButton } from "../../components/PublishToTradeModal";
import { formatIssueDateCell, formatUploadDateCell, todayIsoDate } from "../../utils/fileMetaDisplay";

const PlanFilesList = () => {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const [userRole, setUserRole] = useState("");
  const [files, setFiles] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [folderName, setFolderName] = useState("");
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [operationType, setOperationType] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedPrintFile, setSelectedPrintFile] = useState(null);
  const [temporaryLinkModalOpen, setTemporaryLinkModalOpen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDisplayName, setUploadDisplayName] = useState("");
  const [uploadIssueDate, setUploadIssueDate] = useState("");
  const [uploadAuthor, setUploadAuthor] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  // Edit subfolders modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [editError, setEditError] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFile, setShareFile] = useState(null);

  const project = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const projectId = project ? project.id : null;

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role || !projectId) {
      navigate("/");
    } else {
      setUserRole(role);
      fetchFilesAndFolders();
      fetchBreadcrumbs();
    }
  }, [navigate, projectId, folderId]);

  const fetchFilesAndFolders = async () => {
    try {
      console.log('Fetching files and folders for folder:', folderId);
      const project = JSON.parse(localStorage.getItem("selectedProject"));
      const projectId = project ? project.id : null;

      // 获取文件
      const filesRes = await fetch(`/api/files/plan?folderId=${folderId}&projectId=${projectId}`);
      const filesData = await filesRes.json();

      // 获取子文件夹
      const foldersRes = await fetch(`/api/folders?module=plan&parent_id=${folderId}&projectId=${projectId}`);
      const foldersData = await foldersRes.json();

      if (!filesRes.ok) {
        throw new Error(filesData.error || "Failed to fetch files");
      }

      if (!Array.isArray(filesData.files)) {
        throw new Error("Invalid response structure: files is not an array");
      }

      setFiles(filesData.files);
      setFolderName(filesData.folder?.name || "");
      setCurrentFolder(filesData.folder || null);

      // 设置子文件夹
      if (foldersRes.ok) {
        // 处理不同的API响应格式
        let foldersArray = [];
        if (Array.isArray(foldersData)) {
          foldersArray = foldersData;
        } else if (foldersData.folders && Array.isArray(foldersData.folders)) {
          foldersArray = foldersData.folders;
        }
        setSubfolders(foldersArray);
      } else {
        console.error('Failed to fetch subfolders:', foldersData);
        setSubfolders([]);
      }
    } catch (err) {
      console.error("Error fetching files and folders:", err.message);
    }
  };

  const fetchBreadcrumbs = async () => {
    try {
      const response = await fetch(`/api/folders/${folderId}/breadcrumbs`);
      if (response.ok) {
        const data = await response.json();
        setBreadcrumbs(data);
      }
    } catch (err) {
      console.error("Failed to fetch breadcrumbs:", err);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadMessage("");
    setUploadError("");

    if (!uploadFile || !uploadDisplayName.trim() || !uploadIssueDate) {
      setUploadError("Please select a file, enter display name, and issue date.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("folderId", folderId);
    formData.append("projectId", projectId);
    formData.append("display_name", uploadDisplayName.trim());
    formData.append("issue_date", uploadIssueDate);
    formData.append("author", uploadAuthor);
    formData.append("user_id", localStorage.getItem("userId"));

    try {
      const response = await fetch("/api/upload/plan", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Upload failed");
      }

      await response.json();
      setUploadMessage("File uploaded successfully!");
      setUploadFile(null);
      setUploadDisplayName("");
      setUploadIssueDate(todayIsoDate());
      setUploadAuthor("");

      // 3秒后关闭模态框并刷新文件列表
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadMessage("");
        fetchFilesAndFolders();
      }, 2000);
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err.message || "Failed to upload file");
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setUploadFile(files[0]);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      const res = await fetch(`/api/files/plan/${fileId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete file");
      fetchFilesAndFolders();
    } catch (err) {
      console.error(err);
    }
  };

  const handleMoveCopy = async () => {
    console.log("handleMoveCopy called with:", selectedFile, operationType);
    setIsModalOpen(false);
    setSelectedFile(null);
    setOperationType(null);
    fetchFilesAndFolders();
  };

  const handlePrintRequest = (file) => {
    setSelectedPrintFile(file);
    setIsPrintModalOpen(true);
  };

  const submitPrintRequest = async (printRequestData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const requestData = {
        ...printRequestData,
        module: 'plan'
      };

      const response = await fetch('/api/print-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData),
      });

      if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error submitting print request:', error);
      throw error;
    }
  };

  // Edit subfolders functions
  const handleAddSubfolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    try {
      const response = await fetch("/api/folders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newFolderName.trim(),
          module: "plan",
          projectId: projectId,
          parent_id: folderId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create folder");
      }

      setNewFolderName("");
      fetchFilesAndFolders(); // Refresh both files and subfolders
      setEditError("");
    } catch (err) {
      console.error("Add folder error:", err);
      setEditError(err.message || "Failed to create folder");
    }
  };

  const handleDeleteSubfolder = async (subfolderId) => {
    if (!window.confirm("Are you sure you want to delete this subfolder and all its contents?")) return;

    try {
      const response = await fetch(`/api/folders/${subfolderId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete folder");
      }
      fetchFilesAndFolders();
      setEditError("");
    } catch (err) {
      console.error("Delete folder error:", err);
      setEditError(err.message || "Failed to delete folder");
    }
  };

  const handleUpdateSubfolder = async (subfolderId) => {
    if (!editingFolderName.trim()) return;
    try {
      const response = await fetch(`/api/folders/${subfolderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: editingFolderName.trim(),
          projectId: projectId,
          module: "plan" 
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update folder");
      }
      setEditingFolderId(null);
      setEditingFolderName("");
      fetchFilesAndFolders();
      setEditError("");
    } catch (err) {
      console.error("Update folder error:", err);
      setEditError(err.message || "Failed to update folder");
    }
  };

  const getBackPath = () => {
    if (breadcrumbs.length === 0) {
      return "/planfile_page";
    }
    const parentBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
    return parentBreadcrumb ? `/planfile_list/${parentBreadcrumb._id}` : "/planfile_page";
  };

  const handleFolderClick = (folder) => {
    // 使用混合视图，所有子文件夹都导航到文件列表页面
    navigate(`/planfile_list/${folder._id}`);
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="plan" />

      <div className="flex-1 p-6 bg-gray-50 text-[12px] ml-[60px]">
        {/* 页面标题 */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[18px]">Plan Files{folderName ? ` - ${folderName}` : ""}</h2>
          <img src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" className="h-6" />
        </div>

        {/* 面包屑导航 */}
        {breadcrumbs.length > 0 && (
          <nav className="mb-4">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li>
                <button
                  onClick={() => navigate("/planfile_page")}
                  className="text-blue-600 hover:underline"
                >
                  Root
                </button>
              </li>
              {breadcrumbs.map((breadcrumb, index) => (
                <React.Fragment key={breadcrumb._id}>
                  <li className="text-gray-400">/</li>
                  <li>
                    {index === breadcrumbs.length - 1 ? (
                      <span className="text-gray-900 font-medium">{breadcrumb.name}</span>
                    ) : (
                      <button
                        onClick={() => navigate(`/planfile_list/${breadcrumb._id}`)}
                        className="text-blue-600 hover:underline"
                      >
                        {breadcrumb.name}
                      </button>
                    )}
                  </li>
                </React.Fragment>
              ))}
            </ol>
          </nav>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => {
              setUploadIssueDate(todayIsoDate());
              setShowUploadModal(true);
            }}
            className="text-white px-3 py-1 rounded text-[12px]"
            style={{ backgroundColor: "#6FA944" }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
          >
            Upload File to {folderName}
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="text-white px-3 py-1 rounded text-[12px]"
            style={{ backgroundColor: "#6FA944" }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
          >
            Edit Subfolders
          </button>
          <button
            onClick={() => navigate(getBackPath())}
            className="bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400 text-[12px]"
          >
            Back
          </button>
        </div>

        {/* 混合显示：子文件夹区域 */}
        {subfolders.length > 0 && (
          <div className="mb-8">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex flex-wrap gap-4">
                {subfolders.map((folder) => (
                  <div
                    key={folder._id}
                    className="w-48 h-20 p-2 flex flex-col items-center cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => handleFolderClick(folder)}
                  >
                    <img
                      src={
                        (folder.totalFileCount || folder.fileCount) > 0
                          ? "/images/folder-withfile.png"
                          : "/images/folder-empty.png"
                      }
                      alt="Folder"
                      className="w-8 h-8 mb-2"
                    />
                    <p className="text-center text-sm font-medium text-[10px] break-words">
                      {folder.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 混合显示：当前文件夹文件区域 */}
        <div className="mb-6">
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="p-4"></div>

            <div className="p-4">
              {files.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No files in current folder
                </div>
              ) : (
                <table className="min-w-full bg-white text-[12px]">
                  <thead>
                    <tr className="bg-gray-100 text-sm text-left text-[12px]">
                      <th className="px-4 py-2">Display / file name</th>
                      <th className="px-4 py-2">Author</th>
                      <th className="px-4 py-2">Issue date</th>
                      <th className="px-4 py-2">Size</th>
                      <th className="px-4 py-2">Format</th>
                      <th className="px-4 py-2">Upload date</th>
                      <th className="px-4 py-2">Upload by</th>
                      <th className="px-4 py-2">Move/Copy</th>
                      <th className="px-4 py-2">Trade</th>
                      <th className="px-4 py-2">Print</th>
                      <th className="px-4 py-2">Share</th>
                      <th className="px-4 py-2">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => (
                      <tr key={file._id} className="border-t text-sm text-[12px] hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <FileListNameCell file={file} module="plan" />
                        </td>
                        <td className="px-4 py-2">{file.author || "—"}</td>
                        <td className="px-4 py-2">{formatIssueDateCell(file)}</td>
                        <td className="px-4 py-2">
                          {(file.size || file.file_size) ? 
                            `${((file.size || file.file_size) / (1024 * 1024)).toFixed(2)} MB` : 
                            "0.00 MB"}
                        </td>
                        <td className="px-4 py-2">{file.format}</td>
                        <td className="px-4 py-2">{formatUploadDateCell(file)}</td>
                        <td className="px-4 py-2">{file.upload_by || "-"}</td>
                        <td className="px-4 py-2">
                          <div className="relative inline-block text-left">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const dropdown = e.currentTarget.nextElementSibling;
                                dropdown.classList.toggle("hidden");
                              }}
                              className="text-blue-600 hover:text-blue-800 px-3 py-1 rounded"
                            >
                              ▶
                            </button>
                            <div className="hidden absolute z-10 mt-2 w-40 bg-white rounded-md shadow-lg">
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFile(file);
                                    setOperationType("move");
                                    setIsModalOpen(true);
                                    e.currentTarget.parentElement.parentElement.classList.add(
                                      "hidden",
                                    );
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Move to...
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFile(file);
                                    setOperationType("copy");
                                    setIsModalOpen(true);
                                    e.currentTarget.parentElement.parentElement.classList.add(
                                      "hidden",
                                    );
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Copy to...
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <PublishToTradeButton
                            file={file}
                            module="plan"
                            projectId={projectId}
                            userRole={userRole}
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button 
                            className="text-gray-600 hover:underline"
                            onClick={() => handlePrintRequest(file)}
                          >🖨</button>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            className="text-blue-600 hover:text-blue-800"
                            onClick={() => {
                              setShareFile(file);
                              setShowShareModal(true);
                            }}
                            title="Share"
                          >
                            <FaShareAlt size={14} />
                          </button>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            className="text-red-600 hover:underline"
                            onClick={() => handleDelete(file._id)}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* 模态框组件 */}
        <FileOperationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          file={{...selectedFile, module: 'plan'}}
          operation={operationType}
          onConfirm={handleMoveCopy}
        />

        <PrintRequestModal
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          file={selectedPrintFile}
          onSubmit={submitPrintRequest}
        />

        {/* Temporary Link Modal */}
        {temporaryLinkModalOpen && (
          <TemporaryLinkModal
            isOpen={temporaryLinkModalOpen}
            onClose={() => setTemporaryLinkModalOpen(false)}
            file={selectedFile}
          />
        )}

        {/* Share Link Modal */}
        <ShareLinkModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setShareFile(null);
          }}
          file={shareFile}
          module="plan"
        />

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-[480px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Upload File to {folderName}
                </h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadDisplayName("");
                    setUploadIssueDate("");
                    setUploadAuthor("");
                    setUploadMessage("");
                    setUploadError("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {uploadError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {uploadError}
                </div>
              )}

              {uploadMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  {uploadMessage}
                </div>
              )}

              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File
                  </label>
                  <div
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => document.getElementById('fileInput').click()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      id="fileInput"
                      type="file"
                      accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png,.dwg,.mov,.mp4,.avi,.wmv,.flv,.mkv,.webm,.zip,.rar"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="hidden"
                    />
                    <div className="text-gray-600">
                      {uploadFile ? (
                        <p className="font-medium text-blue-600">{uploadFile.name}</p>
                      ) : (
                        <div>
                          <p className="mb-1">Drag and drop file here</p>
                          <p className="text-xs text-gray-400 mb-1">Allowed: PDF, DOCX, XLSX, JPG, PNG, DWG, video, ZIP, RAR</p>
                          <p className="text-xs text-gray-400">or click to browse</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <FileUploadMetaFields
                  displayName={uploadDisplayName}
                  onDisplayNameChange={setUploadDisplayName}
                  issueDate={uploadIssueDate}
                  onIssueDateChange={setUploadIssueDate}
                  author={uploadAuthor}
                  onAuthorChange={setUploadAuthor}
                  ids={{
                    display: "plan-upload-display-name",
                    issue: "plan-upload-issue-date",
                    author: "plan-upload-author",
                  }}
                />

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadFile(null);
                      setUploadDisplayName("");
                      setUploadIssueDate("");
                      setUploadAuthor("");
                      setUploadMessage("");
                      setUploadError("");
                    }}
                    className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 text-[12px]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="text-white px-4 py-2 rounded text-[12px]"
                    style={{ backgroundColor: "#6FA944" }}
                    onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
                    onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
                  >
                    Upload File
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Subfolders Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-[600px] max-w-[90vw] max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Edit Subfolders in {folderName}
                </h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setNewFolderName("");
                    setEditingFolderId(null);
                    setEditingFolderName("");
                    setEditError("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {editError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {editError}
                </div>
              )}

              {/* Add new subfolder form */}
              <form onSubmit={handleAddSubfolder} className="mb-6 p-4 bg-gray-50 rounded">
                <h4 className="text-md font-medium mb-3">Add New Subfolder</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Subfolder name"
                    className="flex-1 p-2 border rounded text-[12px]"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-[12px]"
                  >
                    Add
                  </button>
                </div>
              </form>

              {/* Existing subfolders list */}
              <div>
                <h4 className="text-md font-medium mb-3">Existing Subfolders</h4>
                {subfolders.length === 0 ? (
                  <p className="text-gray-500">No subfolders found.</p>
                ) : (
                  <div className="space-y-2">
                    {subfolders.map((folder) => (
                      <div
                        key={folder._id}
                        className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Level {folder.level}
                          </span>
                          {editingFolderId === folder._id ? (
                            <input
                              type="text"
                              value={editingFolderName}
                              onChange={(e) => setEditingFolderName(e.target.value)}
                              className="p-1 border rounded text-[12px]"
                              autoFocus
                            />
                          ) : (
                            <span className="font-medium">{folder.name}</span>
                          )}
                          <span className="text-xs text-gray-500">
                            ({folder.fileCount || 0} files)
                          </span>
                        </div>

                        <div className="flex gap-2">
                          {editingFolderId === folder._id ? (
                            <>
                              <button
                                onClick={() => handleUpdateSubfolder(folder._id)}
                                className="bg-green-500 text-white px-2 py-1 rounded text-[12px] hover:bg-green-600"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingFolderId(null);
                                  setEditingFolderName("");
                                }}
                                className="bg-gray-500 text-white px-2 py-1 rounded text-[12px] hover:bg-gray-600"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingFolderId(folder._id);
                                  setEditingFolderName(folder.name);
                                }}
                                className="bg-blue-500 text-white px-2 py-1 rounded text-[12px] hover:bg-blue-600"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteSubfolder(folder._id)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-[12px] hover:bg-red-600"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setNewFolderName("");
                    setEditingFolderId(null);
                    setEditingFolderName("");
                    setEditError("");
                  }}
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 text-[12px]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanFilesList;
